import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase, AlertTriangle, FileCheck, ClipboardList, DollarSign, Bell, Filter, PenTool,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const severityBadgeStyles = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export const DashboardActionCenter = ({
  className = '',
  actionItems: externalActionItems,
  actionLoading: externalLoading,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [internalItems, setInternalItems] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const useExternal = externalActionItems !== undefined;
  const actionItems = useExternal ? externalActionItems : internalItems;
  const actionLoading = useExternal ? Boolean(externalLoading) : internalLoading;
  const [actionCenterOpen, setActionCenterOpen] = useState(false);
  const [actionFilterModule, setActionFilterModule] = useState('all');
  const [actionFilterSeverity, setActionFilterSeverity] = useState('all');
  const [actionSubmitting, setActionSubmitting] = useState('');
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [remindActionId, setRemindActionId] = useState(null);
  const [customRemindAt, setCustomRemindAt] = useState('');
  const [remindMode, setRemindMode] = useState('datetime');
  const [customRemindValue, setCustomRemindValue] = useState('');
  const [customRemindUnit, setCustomRemindUnit] = useState('hours');

  const fetchActionCenter = async () => {
    if (useExternal) {
      onRefresh?.();
      return;
    }
    try {
      setInternalLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/dashboard/action-center`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInternalItems(response.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch action center:', error);
    } finally {
      setInternalLoading(false);
    }
  };

  useEffect(() => {
    if (!useExternal) {
      fetchActionCenter();
    }
  }, [useExternal]);

  useEffect(() => {
    const handler = (event) => {
      openActionCenter(event.detail?.module || 'all');
    };
    window.addEventListener('dashboard-open-action-center', handler);
    return () => window.removeEventListener('dashboard-open-action-center', handler);
  }, [actionItems.length]);

  const actionModuleCounts = useMemo(() => {
    return actionItems.reduce((acc, action) => {
      const moduleKey = action.module || 'Other';
      acc[moduleKey] = (acc[moduleKey] || 0) + 1;
      return acc;
    }, {});
  }, [actionItems]);

  const moduleFilterOptions = useMemo(() => ['all', ...Object.keys(actionModuleCounts)], [actionModuleCounts]);

  const filteredActions = useMemo(() => {
    return actionItems.filter((action) => {
      const moduleMatch = actionFilterModule === 'all' || action.module === actionFilterModule;
      const severityMatch = actionFilterSeverity === 'all' || action.severity === actionFilterSeverity;
      return moduleMatch && severityMatch;
    });
  }, [actionItems, actionFilterModule, actionFilterSeverity]);

  const openActionCenter = (moduleKey = 'all') => {
    setActionFilterModule(moduleKey);
    setActionFilterSeverity('all');
    setActionCenterOpen(true);
  };

  const handleActionUpdate = async (actionId, type, payload = {}) => {
    try {
      setActionSubmitting(`${actionId}:${type}`);
      const token = localStorage.getItem('access_token');
      const encodedId = encodeURIComponent(actionId);
      const headers = { Authorization: `Bearer ${token}` };
      if (type === 'acknowledge') {
        await axios.post(`${API}/dashboard/action-center/${encodedId}/acknowledge`, {}, { headers });
      }
      if (type === 'complete') {
        await axios.post(`${API}/dashboard/action-center/${encodedId}/complete`, {}, { headers });
      }
      if (type === 'remind') {
        await axios.post(`${API}/dashboard/action-center/${encodedId}/remind`, payload, { headers });
      }
      await fetchActionCenter();
    } catch (error) {
      console.error('Failed to update action item:', error);
    } finally {
      setActionSubmitting('');
    }
  };

  const handleCustomRemind = async () => {
    if (!remindActionId) return;
    if (remindMode === 'datetime') {
      if (!customRemindAt) return;
      const remindDate = new Date(customRemindAt);
      if (Number.isNaN(remindDate.getTime())) return;
      await handleActionUpdate(remindActionId, 'remind', { remind_at: remindDate.toISOString() });
    } else {
      const value = parseInt(customRemindValue, 10);
      if (!value || value < 1) return;
      if (customRemindUnit === 'hours') {
        await handleActionUpdate(remindActionId, 'remind', { remind_in_hours: value });
      } else {
        await handleActionUpdate(remindActionId, 'remind', { remind_in_days: value });
      }
    }
    setRemindDialogOpen(false);
    setCustomRemindAt('');
    setCustomRemindValue('');
    setRemindActionId(null);
  };

  if (actionItems.length === 0 && !actionLoading) {
    return null;
  }

  return (
    <div className={`flex items-center justify-end gap-2 ${className}`}>
      <Sheet open={actionCenterOpen} onOpenChange={setActionCenterOpen}>
        <SheetTrigger asChild>
          <Button
            variant="secondary"
            onClick={() => openActionCenter('all')}
            data-testid="open-action-center-button"
            className="flex items-center gap-2"
          >
            <Bell size={16} />
            Action Center
            <Badge variant="secondary" data-testid="action-center-button-count">
              {actionItems.length}
            </Badge>
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-[90vw] sm:w-[760px] max-w-[90vw] sm:max-w-[760px] overflow-hidden"
          data-testid="action-center-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-xl">Action Center</SheetTitle>
            <SheetDescription>Focused actions that require follow-up.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4 h-full flex flex-col min-h-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[150px] flex-1">
                <Select value={actionFilterModule} onValueChange={setActionFilterModule}>
                  <SelectTrigger data-testid="action-center-filter-module">
                    <SelectValue placeholder="Filter by module" />
                  </SelectTrigger>
                  <SelectContent>
                    {moduleFilterOptions.map((module) => (
                      <SelectItem key={module} value={module}>
                        {module === 'all' ? 'All Modules' : module}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Select value={actionFilterSeverity} onValueChange={setActionFilterSeverity}>
                  <SelectTrigger data-testid="action-center-filter-severity">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setActionFilterModule('all');
                  setActionFilterSeverity('all');
                }}
                data-testid="action-center-clear-filters"
              >
                <Filter size={14} className="mr-1" />
                Clear
              </Button>
            </div>

            <div
              className="flex-1 min-h-0 overflow-y-auto pr-2 pb-10 space-y-2 max-h-[calc(100vh-240px)]"
              data-testid="action-center-list"
            >
              {actionLoading ? (
                <div className="text-sm text-muted-foreground">Loading action items...</div>
              ) : filteredActions.length === 0 ? (
                <div className="text-sm text-muted-foreground">No action items match the current filters.</div>
              ) : (
                filteredActions.map((action) => (
                  <div
                    key={action.id}
                    className="rounded-lg border border-slate-200 bg-white p-2 space-y-1"
                    data-testid={`action-center-item-${action.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{action.title}</p>
                      <div className="flex items-center gap-2">
                        <Badge className={severityBadgeStyles[action.severity] || severityBadgeStyles.medium}>
                          {(action.severity || 'medium').toUpperCase()}
                        </Badge>
                        <Badge variant="secondary">{action.module}</Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                      <p className="max-w-[60%] truncate">{action.description}</p>
                      <span>
                        Due: {action.due_at ? new Date(action.due_at).toLocaleDateString() : 'No due date'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleActionUpdate(action.id, 'acknowledge')}
                        disabled={actionSubmitting === `${action.id}:acknowledge`}
                      >
                        Acknowledge
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleActionUpdate(action.id, 'complete')}
                        disabled={actionSubmitting === `${action.id}:complete`}
                      >
                        Mark Complete
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                            Remind
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleActionUpdate(action.id, 'remind', { remind_in_hours: 1 })}>
                            In 1 hour
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleActionUpdate(action.id, 'remind', { remind_in_hours: 4 })}>
                            In 4 hours
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleActionUpdate(action.id, 'remind', { remind_in_days: 1 })}>
                            In 1 day
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleActionUpdate(action.id, 'remind', { remind_in_days: 3 })}>
                            In 3 days
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleActionUpdate(action.id, 'remind', { remind_in_days: 7 })}>
                            In 7 days
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setRemindActionId(action.id);
                              setRemindMode('datetime');
                              setCustomRemindAt('');
                              setRemindDialogOpen(true);
                            }}
                          >
                            Pick date & time
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setRemindActionId(action.id);
                              setRemindMode('duration');
                              setCustomRemindValue('');
                              setRemindDialogOpen(true);
                            }}
                          >
                            Custom duration
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          if (action.route) {
                            navigate(action.route);
                            setActionCenterOpen(false);
                          }
                        }}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={remindDialogOpen} onOpenChange={setRemindDialogOpen}>
        <DialogContent data-testid="action-center-remind-dialog">
          <DialogHeader>
            <DialogTitle>Pick a reminder time</DialogTitle>
            <DialogDescription>
              Choose a specific date and time to be reminded about this action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant={remindMode === 'datetime' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setRemindMode('datetime')}
              >
                Specific date
              </Button>
              <Button
                variant={remindMode === 'duration' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setRemindMode('duration')}
              >
                Custom duration
              </Button>
            </div>
            {remindMode === 'datetime' ? (
              <div className="space-y-2">
                <Label htmlFor="remind-at">Remind at</Label>
                <Input
                  id="remind-at"
                  type="datetime-local"
                  value={customRemindAt}
                  onChange={(event) => setCustomRemindAt(event.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="remind-duration">Remind in</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="remind-duration"
                    type="number"
                    min="1"
                    value={customRemindValue}
                    onChange={(event) => setCustomRemindValue(event.target.value)}
                  />
                  <Select value={customRemindUnit} onValueChange={setCustomRemindUnit}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRemindDialogOpen(false);
                setCustomRemindAt('');
                setCustomRemindValue('');
                setCustomRemindUnit('hours');
                setRemindActionId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCustomRemind}
              disabled={!remindActionId || (remindMode === 'datetime' ? !customRemindAt : !customRemindValue)}
            >
              Set Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Module notice configs derived from action center counts */
export const ACTION_MODULE_NOTICE_CONFIGS = [
  { module: 'Recruitment', title: 'Recruitment Actions', icon: Briefcase, variant: 'warning', description: 'Interviews, offers, and candidate follow-ups.' },
  { module: 'Incidents', title: 'Incident Follow-ups', icon: AlertTriangle, variant: 'error', description: 'Incident reports waiting review or action.' },
  { module: 'HRM', title: 'HR & Compliance', icon: FileCheck, variant: 'warning', description: 'Compliance documents and policy reviews.' },
  { module: 'NDIS', title: 'NDIS Agreements', icon: ClipboardList, variant: 'warning', description: 'Quotes, agreements, and funding expiries.' },
  { module: 'Invoicing', title: 'Invoice Follow-ups', icon: DollarSign, variant: 'warning', description: 'Invoices due soon or overdue.' },
  { module: 'E-Sign', title: 'E-Signatures', icon: PenTool, variant: 'warning', description: 'Pending, in-progress, and expired signature requests.' },
];

export default DashboardActionCenter;
